import React, { Fragment, useState } from 'react';
import { Link, withRouter } from 'react-router-dom';
import { Icon, Money, Translate, Skeleton, Button } from 'tg-core-components';
import { withConfig } from '@config';
import { useUrls } from '@utils/urls';
import { useConfig } from '@utils/config';
import cn from 'classnames';

import './style.css';
import { useDispatch } from 'react-redux';
import devcodeActions from 'tg-core-redux/lib/modules/devcode/action';
import { useIntl } from 'react-intl';

export const selectMembershipActionsProps = state => ({
  isAuthenticated: state.player.isAuthenticated,
  wallet: state.wallet,
  currency: state.player.user?.Currency,
  activeCurrency: state.player.activeCurrency,
  user: state.player.user,
  paymentTransactions: state.paymentTransactions,
  sessionId: state.player.sessionId,
});

const WalletButtonSkeleton = () => (
  <div className="WalletButtonSkeleton">
    <Skeleton.SkeletonLine />
  </div>
);

const MembershipActions = ({
  /* props from selector */
  isAuthenticated,
  wallet,
  currency,
  activeCurrency,
  user,
  paymentTransactions,
  sessionId,

  /* withConfig */
  config,

  /* props from parent */
  ref_url: refUrl,
  jurisdiction,

  /* Props from withRouter */
  history,
}) => {
  const { deposit, signIn, signUp } = useUrls();
  const intl = useIntl();

  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();

  const pendingWithdrawal = paymentTransactions?.data
    ?.filter(
      transaction =>
        transaction.Status === 'initiated' &&
        transaction.TransactionType === 'Withdraw'
    )
    ?.sort((a, b) => b.Amount - a.Amount)[0];

  const formattedAmount = intl?.formatNumber(pendingWithdrawal?.Amount, {
    style: 'currency',
    curreny: currency,
  });

  const onSubmit = e => {
    e.preventDefault();

    setIsLoading(true);

    return dispatch(
      devcodeActions.cancelUserWithdrawal({
        transactionId: pendingWithdrawal.RemoteTransactionId,
        userId: user?.Id,
        merchantId: config.MERCHANT_ID,
        sessionId,
      })
    )
      .then(res => {
        if (res?.data?.success) {
          return history.push({
            pathname: '/casino',
            hash: 'popup/successful-deposit',
          });
        }
      })
      .finally(() => setIsLoading(false));
  };

  return (
    <div
      className={cn(
        'MembershipActions',
        pendingWithdrawal ? 'withdraw-pending' : ''
      )}>
      {isAuthenticated ? (
        <>
          {pendingWithdrawal &&
            config.cancelWithdrawal &&
            jurisdiction === 'mga' && (
              <form onSubmit={onSubmit}>
                <Button
                  className="button secondary"
                  type="submit"
                  disabled={isLoading}
                  isLoading={isLoading}>
                  <Translate
                    id="action.cancel-withdrawal"
                    defaultMessage="Cancel withdrawal"
                    values={{
                      amount: formattedAmount,
                    }}
                  />
                  <Icon icon="cancel-withdraw" />
                </Button>
              </form>
            )}
          <WalletButton
            user={user}
            wallet={wallet}
            currency={activeCurrency || currency}
            isAuthenticated={isAuthenticated}
            refUrl={refUrl}
          />
        </>
      ) : config.isPnp ? (
        <Fragment>
          <Link
            className="button primary"
            to={deposit({ search: `?ref_url=${encodeURIComponent(refUrl)}` })}>
            <Translate id="action.pay_and_play" defaultMessage="Play now" />
          </Link>
          <Link
            to={signIn({ search: `?ref_url=${encodeURIComponent(refUrl)}` })}
            className="button secondary continue">
            <Translate
              id="action.fetch_balance"
              defaultMessage="Continue playing"
            />
          </Link>
        </Fragment>
      ) : (
        <Fragment>
          <Link className="button primary" to={signUp()}>
            <Translate id="action.sign-up" defaultMessage="Create account" />
          </Link>
          <Link className="button secondary" to={signIn()}>
            <Translate id="action.sign-in" defaultMessage="Sign in" />
          </Link>
        </Fragment>
      )}
    </div>
  );
};

export default withConfig(withRouter(MembershipActions));

const WalletButton = ({ wallet, currency, isAuthenticated, refUrl, user }) => {
  const { deposit } = useUrls();

  const config = useConfig();

  if (!user || !wallet) {
    return <WalletButtonSkeleton />;
  }

  return (
    <Link
      className="button icon primary user-balance"
      to={deposit({ search: `?ref_url=${encodeURIComponent(refUrl)}` })}>
      {wallet.isLoading && <span>...</span>}
      {!wallet.isLoading && wallet.data && isAuthenticated && (
        <Fragment>
          <div className="balance">
            {config.showMembershipBonusBalance ? (
              <>
                <div className="money-balance">
                  <div className="balance-label">
                    <Translate
                      id="label.header-balance"
                      defaultMessage="Cash:"
                    />
                  </div>
                  <Money
                    value={wallet.data.Total.Balance}
                    currency={currency}
                  />
                </div>
                <div className="bonus-balance">
                  <div className="balance-label">
                    <Translate
                      id="label.header-bonus-balance"
                      defaultMessage="Bonus:"
                    />
                  </div>
                  <Money
                    value={wallet.data.Bonus.Balance}
                    currency={currency}
                  />
                </div>
              </>
            ) : (
              <Money value={wallet.data.Total.Balance} currency={currency} />
            )}
          </div>
        </Fragment>
      )}
      <div className="line" />
      <Icon icon="plus" />
    </Link>
  );
};
